import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface Profile {
  id: string;
  user_id: string;
  cnpj: string | null;
  razao_social: string | null;
  nome_fantasia: string | null;
  endereco: string | null;
  cidade: string | null;
  estado: string | null;
  cep: string | null;
  telefone: string | null;
  cadastro_completo: boolean;
}

export interface RetailerProfile {
  id: string;
  user_id: string;
  cnpj: string;
  store_name: string;
  phone: string | null;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  quote_validity_days: number;
  installment_options: string[];
  instagram: string | null;
  whatsapp: string | null;
  facebook: string | null;
  tiktok: string | null;
  address: string | null;
  salespeople: string[];
  goal_value: number | null;
  goal_type: string;
  observation_templates: string[];
  is_approved: boolean;
  is_active: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  retailerProfile: RetailerProfile | null;
  loading: boolean;
  isAdmin: boolean;
  isStaff: boolean;
  isSuperAdmin: boolean;
  permissions: string[];
  rolesReady: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  updateRetailerProfile: (updates: Partial<RetailerProfile>) => Promise<{ error: Error | null }>;
  isFullyRegistered: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  profile: null,
  retailerProfile: null,
  loading: true,
  isAdmin: false,
  isStaff: false,
  isSuperAdmin: false,
  permissions: [],
  rolesReady: false,
  signOut: async () => {},
  refreshProfile: async () => {},
  updateRetailerProfile: async () => ({ error: null }),
  isFullyRegistered: false,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [retailerProfile, setRetailerProfile] = useState<RetailerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [rolesReady, setRolesReady] = useState(false);

  const fetchProfile = async (userId: string) => {
    setRolesReady(false);
    try {
      const { data } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", userId)
        .single();
      setProfile(data as Profile | null);

      const [{ data: adminFlag }, { data: staffFlag }, { data: superFlag }] = await Promise.all([
        supabase.rpc("has_role", { _user_id: userId, _role: "admin" }),
        supabase.rpc("has_role" as any, { _user_id: userId, _role: "staff" }),
        supabase.rpc("is_super_admin" as any),
      ]);
      setIsAdmin(!!adminFlag);
      setIsStaff(!!staffFlag);
      setIsSuperAdmin(!!superFlag);
      if (staffFlag) {
        const { data: perms } = await supabase.rpc("get_my_permissions" as any);
        setPermissions(((perms as any[]) || []).map((p: { area: string }) => p.area));
      } else {
        setPermissions([]);
      }
    } catch (err) {
      console.error("fetchProfile error:", err);
      setProfile(null);
      setIsAdmin(false);
      setIsStaff(false);
      setIsSuperAdmin(false);
      setPermissions([]);
    } finally {
      setRolesReady(true);
    }
  };

  const fetchRetailerProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("retailer_profiles")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) throw error;
      if (data) {
        setRetailerProfile({
          ...data,
          primary_color: data.primary_color || "#1a365d",
          secondary_color: data.secondary_color || "#c6975c",
          quote_validity_days: data.quote_validity_days || 7,
          installment_options: data.installment_options || [],
          instagram: data.instagram || null,
          whatsapp: data.whatsapp || null,
          facebook: data.facebook || null,
          tiktok: data.tiktok || null,
          address: data.address || null,
          salespeople: data.salespeople || [],
          goal_value: data.goal_value || null,
          goal_type: data.goal_type || "value",
          observation_templates: data.observation_templates || [
            "Prazo de entrega: 7 dias úteis.",
            "Garantia de 1 ano contra defeitos de fabricação.",
            "Condições especiais para pagamento à vista."
          ],
          is_approved: data.is_approved ?? false,
          is_active: data.is_active ?? true,
        });
      } else {
        setRetailerProfile(null);
      }
    } catch (error) {
      console.error("Error fetching retailer profile:", error);
      setRetailerProfile(null);
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await Promise.all([fetchProfile(user.id), fetchRetailerProfile(user.id)]);
    } else {
      setRolesReady(true);
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          setRolesReady(false);
          setTimeout(() => {
            fetchProfile(session.user.id);
            fetchRetailerProfile(session.user.id);
          }, 0);
        } else {
          setProfile(null);
          setRetailerProfile(null);
          setIsAdmin(false);
          setIsStaff(false);
          setIsSuperAdmin(false);
          setPermissions([]);
          setRolesReady(true);
        }
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        setRolesReady(false);
        fetchProfile(session.user.id);
        fetchRetailerProfile(session.user.id);
      } else {
        setRolesReady(true);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setRetailerProfile(null);
    setIsAdmin(false);
    setIsStaff(false);
    setIsSuperAdmin(false);
    setPermissions([]);
    setRolesReady(true);
  };

  const updateRetailerProfile = async (updates: Partial<RetailerProfile>) => {
    if (!user) return { error: new Error("Usuário não autenticado") };

    const { error } = await supabase
      .from("retailer_profiles")
      .update(updates)
      .eq("user_id", user.id);

    if (!error) {
      await fetchRetailerProfile(user.id);
    }

    return { error: error as Error | null };
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        retailerProfile,
        loading,
        isAdmin,
        isStaff,
        isSuperAdmin,
        permissions,
        rolesReady,
        signOut,
        refreshProfile,
        updateRetailerProfile,
        isFullyRegistered: !!profile?.cadastro_completo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
